import { Injectable } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

export const SILENT_ERROR = new HttpContextToken<boolean>(() => false);
import { catchError, Observable, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { AuthService } from '@auth0/auth0-angular';

/** One entry of FastAPI's 422 body: `{"detail": [{loc, msg, type}, ...]}`. */
interface ValidationErrorItem {
    loc?: (string | number)[];
    msg?: string;
}

/** Shape of the errors `@auth0/auth0-spa-js` throws (GenericError & friends). */
interface Auth0Error {
    error?: string;
    error_description?: string;
    message?: string;
}

/**
 * Auth0 SDK error codes that all mean the same thing to us: the session can no longer be
 * renewed silently, so the user has to sign in again.
 *
 * `invalid_grant` is the one behind the 403 from `POST /oauth/token` — the stored refresh
 * token was revoked, rotated away or expired ("Unknown or invalid refresh token.").
 */
const AUTH0_SESSION_EXPIRED_CODES = new Set([
    'invalid_grant',
    'invalid_refresh_token',
    'missing_refresh_token',
    'login_required',
    'consent_required',
    'interaction_required',
]);

/**
 * `AuthHttpInterceptor` is registered *after* this interceptor, so when its call to
 * `getAccessTokenSilently()` fails it throws a raw Auth0 error (not an `HttpErrorResponse`)
 * back up through our `catchError`. Without this check it fell into the generic branch and
 * rendered one sticky "Unexpected Error — Unknown or invalid refresh token." toast per
 * in-flight request.
 */
export function isAuth0SessionExpiredError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) return false;
    const code = (error as Auth0Error)?.error;
    return typeof code === 'string' && AUTH0_SESSION_EXPIRED_CODES.has(code);
}

/**
 * Turn whatever the backend put in the error body into a single human-readable line.
 *
 * FastAPI answers a failed pydantic validator with a *list* of error objects, so handing
 * `error.error.detail` straight to the toast rendered "[object Object]".
 */
export function extractErrorDetail(error: HttpErrorResponse): string {
    const body = error.error;
    const raw = body?.message ?? body?.detail ?? body?.['error:'];

    if (typeof raw === 'string' && raw.trim()) return raw;

    if (Array.isArray(raw)) {
        const lines = (raw as ValidationErrorItem[])
            .map((item) => {
                const msg = String(item?.msg ?? '')
                    // pydantic v2 prefixes custom validator messages with "Value error, ".
                    .replace(/^(Value|Type|Assertion) error, /, '')
                    .trim();
                if (!msg) return '';
                // Drop the leading "body"/"query" segment, keep the field name.
                const field = (item?.loc ?? []).slice(1).join('.');
                return field ? `${field}: ${msg}` : msg;
            })
            .filter(Boolean);
        if (lines.length) return lines.join('\n');
    }

    if (raw && typeof raw === 'object') {
        const msg = (raw as ValidationErrorItem).msg;
        if (typeof msg === 'string' && msg.trim()) return msg;
    }

    return error.message;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    /**
     * Guards against N parallel requests each ending the session: the first one wins, the
     * rest are swallowed. The interceptor is a singleton, and the flag dies with the page
     * the `logout()` redirect replaces.
     */
    private sessionEnded = false;

    constructor(
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
        private auth: AuthService
    ) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error: unknown) => {
                if (isAuth0SessionExpiredError(error)) {
                    this.endSession();
                    return throwError(() => error);
                }

                if (!(error instanceof HttpErrorResponse)) {
                    // Something non-HTTP blew up downstream — let the caller deal with it.
                    return throwError(() => error);
                }

                if (!(error.error instanceof ErrorEvent)) {
                    if (error.status === 401) {
                        this.endSession();
                    } else if (error.status === 403 && error.error?.error_code === 'missing_byod_account') {
                        this.localStorageService.set('notAccessToAccount', 'true');
                    } else if (!request.context.get(SILENT_ERROR)) {
                        const detail = extractErrorDetail(error);

                        const errorMessage = {
                            0: {
                                summary: 'Error',
                                detail: 'Server is not responding',
                                severity: 'error',
                            },
                            409: {
                                summary: 'Conflict',
                                detail,
                                severity: 'warn',
                            },
                            422: {
                                summary: 'Invalid Request',
                                detail,
                                severity: 'warn',
                            },
                            500: {
                                summary: 'Server Error',
                                detail: 'Unexpected server error occurred',
                                severity: 'error',
                            },
                        }[error.status] || {
                            summary: 'Unexpected Error',
                            detail,
                            severity: 'error',
                        };

                        this.messageService.add({ sticky: true, ...errorMessage });
                    }
                }
                return throwError(() => error);
            })
        );
    }

    /** Clear the tenant selection, say why once, and bounce to the login screen. */
    private endSession() {
        if (this.sessionEnded) return;
        this.sessionEnded = true;

        this.localStorageService.remove('groupId');
        this.localStorageService.remove('groupName');
        this.localStorageService.remove('organizationId');
        this.localStorageService.remove('organizationName');
        this.localStorageService.remove('assumedRole');

        this.messageService.add({
            severity: 'warn',
            summary: 'Session expired',
            detail: 'Please sign in again.',
            life: 5000,
        });

        this.auth.logout({ logoutParams: { returnTo: `${window.location.origin}/login` } });
    }
}
